<script module lang="ts">
  /** Accent + icon only — the card body stays on surface in every tone. */
  export type ApprovalTone = 'neutral' | 'warning' | 'danger';
  export type ApprovalMeta = { label: string; value: string };
</script>

<script lang="ts">
  // A decision, rendered as a card rather than a bare button + toast: what is
  // about to be committed, the facts it rests on (meta rows), and two
  // clearly-weighted ways out. Used wherever an HR/admin action lands on
  // someone else's record — approving leave, rejecting a request — so the
  // reviewer sees the subject of the decision at the moment they commit it,
  // instead of only afterwards in a toast.
  //
  // Purely presentational: the caller owns the mutation, the busy flag and
  // whatever surface (Modal, inline panel) the card sits in.
  import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-svelte';
  import type { Snippet } from 'svelte';

  const {
    tone = 'neutral',
    title,
    body,
    meta = [],
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
    busy = false,
    error,
    children
  }: {
    tone?: ApprovalTone;
    title: string;
    body?: string;
    meta?: ApprovalMeta[];
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
    /** Disables both actions and swaps the confirm label while in flight. */
    busy?: boolean;
    error?: string;
    /** Extra detail between the meta rows and the actions. */
    children?: Snippet;
  } = $props();

  const ICON = { neutral: CheckCircle2, warning: AlertTriangle, danger: XCircle };
  const Icon = $derived(ICON[tone]);
</script>

<div class="tym-approval tym-approval--{tone}">
  <div class="flex items-start gap-3">
    <span class="tym-approval-icon" aria-hidden="true">
      <Icon class="h-[17px] w-[17px]" strokeWidth={2} />
    </span>
    <div class="min-w-0 flex-1">
      <p class="tym-approval-title">{title}</p>
      {#if body}
        <p class="tym-approval-body">{body}</p>
      {/if}
    </div>
  </div>

  {#if meta.length > 0}
    <dl class="tym-approval-meta">
      {#each meta as m (m.label)}
        <div>
          <dt>{m.label}</dt>
          <dd>{m.value}</dd>
        </div>
      {/each}
    </dl>
  {/if}

  {#if children}
    <div class="mt-3">{@render children()}</div>
  {/if}

  {#if error}
    <div class="tym-error-state mt-3">{error}</div>
  {/if}

  <div class="mt-4 flex justify-end gap-2">
    <button type="button" class="btn-secondary" disabled={busy} onclick={onCancel}>{cancelLabel}</button>
    <button
      type="button"
      class={tone === 'danger' ? 'btn-danger-solid' : 'btn-primary'}
      disabled={busy}
      onclick={onConfirm}
    >
      {busy ? 'Working...' : confirmLabel}
    </button>
  </div>
</div>
